import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  CircularProgress,
  TextField,
  Autocomplete,
  MenuItem,
  Popper,
  Grid,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import MedicationOutlinedIcon from "@mui/icons-material/MedicationOutlined";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useSelector } from "react-redux";
import { showToast } from "../../../utils/toastUtil";
import { fetchExternalMeds } from "../../../connections/getApi";
import { menuItemStyle } from "../../../style/AppointmentStyle";
import { textfieldTextBlueBorder } from "../../../style/PatientDetailsStyle";
import { createExternalMed } from "../../../connections/postApi";
import { updateExternalMed } from "../../../connections/putApi";
import { deleteExternalMed } from "../../../connections/deleteApi";

// ─── Style tokens ──────────────────────────────────────────────────────────────
const autoSx = {
  ...textfieldTextBlueBorder,
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    height: "34px",
    padding: "0 8px !important",
    "& input": { padding: "0 4px", fontSize: "13px", fontFamily: "Lato" },
  },
};

const plainInputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    fontFamily: "Lato",
    fontSize: "13px",
    height: "34px",
    "& input": { padding: "0 8px", fontSize: "13px", fontFamily: "Lato" },
    "& fieldset": { borderColor: "#BBDCE5" },
    "&:hover fieldset": { borderColor: "#0097B2" },
    "&.Mui-focused fieldset": { borderColor: "#0097B2", borderWidth: "2px" },
  },
};

const tealBtnStyle = {
  backgroundColor: "#0097B2",
  color: "#fff",
  borderRadius: "8px",
  textTransform: "none",
  fontFamily: "Lato",
  fontSize: "12px",
  fontWeight: 600,
  "&:hover": { backgroundColor: "#007A99" },
};

const emptyRow = {
  name: "",
  medicine_id: "",
  dosage: "",
  frequency: "",
  doctor_name: "",
  doctor_details: "",
};

// ─── Shared styled Popper ─────────────────────────────────────────────────────
const StyledPopper = (props) => (
  <Grid item lg={8}>
    <Popper
      {...props}
      placement="bottom-start"
      style={{ ...menuItemStyle, width: props.anchorEl?.offsetWidth || "auto" }}
      modifiers={[{ name: "preventOverflow", options: { boundary: "window" } }]}
    >
      <div
        style={{
          maxHeight: 250,
          overflowY: "auto",
          scrollbarWidth: "none",
          width: "100%",
          border: "2px solid #007A99",
          borderTop: "1px solid #007A99",
          msOverflowStyle: "none",
          borderRadius: "20px",
          paddingTop: "7px",
          backgroundColor: "white",
        }}
      >
        {props.children}
      </div>
    </Popper>
  </Grid>
);

// ─── Cell padding constants ───────────────────────────────────────────────────
// Row 1 of a pair: top padding, NO bottom border (merges visually with row 2)
const r1 = { px: 1, pt: "6px", pb: "2px", borderBottom: "none" };
// Row 2 of a pair: small top pad, bottom separator
const r2 = { px: 1, pt: "1px", pb: "5px", borderBottom: "1px solid #efefef" };

// ─── Editable rows ───────────────────────────────────────────────────────────
const EditableRow = ({ index, data, onSave, onCancel, saving }) => {
  const [form, setForm] = useState({ ...emptyRow, ...data });

  const [medSearch, setMedSearch] = useState(data?.name || "");
  const [filteredMeds, setFilteredMeds] = useState([]);
  const [medHighlight, setMedHighlight] = useState(0);
  const [medOpen, setMedOpen] = useState(false);

  const [dosageSearch, setDosageSearch] = useState(data?.dosage || "");
  const [filteredDosages, setFilteredDosages] = useState([]);
  const [dosageHighlight, setDosageHighlight] = useState(0);
  const [dosageOpen, setDosageOpen] = useState(false);

  const set = (field) => (val) => setForm((p) => ({ ...p, [field]: val }));

  const handleMedSearch = (e) => {
    const q = e.target.value;
    setMedSearch(q);
    set("name")(q);
    set("medicine_id")("");
    if (!q) {
      setFilteredMeds([]);
      setMedHighlight(0);
      return;
    }
    const stored = JSON.parse(localStorage.getItem("allMedicines")) || [];
    setFilteredMeds(
      stored
        .filter((m) => m[1]?.toLowerCase().startsWith(q.toLowerCase()))
        .slice(0, 10),
    );
  };
  const selectMed = (o) => {
    setForm((p) => ({ ...p, medicine_id: o[0], name: o[1] }));
    setMedSearch(o[1]);
    setFilteredMeds([]);
    setMedHighlight(0);
    setTimeout(() => document.getElementById(`em-dosage-${index}`)?.focus(), 0);
  };
  const handleMedKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setMedHighlight((p) => (p < filteredMeds.length - 1 ? p + 1 : 0));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setMedHighlight((p) => (p > 0 ? p - 1 : filteredMeds.length - 1));
    } else if (e.key === "Tab" || e.key === "Enter") {
      e.preventDefault();
      const t = filteredMeds[medHighlight];
      if (t) selectMed(t);
      else
        setTimeout(
          () => document.getElementById(`em-dosage-${index}`)?.focus(),
          0,
        );
    }
  };

  const handleDosageSearch = (e) => {
    const raw = e.target.value;
    setDosageSearch(raw);
    set("dosage")(raw);
    const q =
      raw === "1"
        ? "once"
        : raw === "2"
          ? "twice"
          : raw === "3"
            ? "thrice"
            : raw;
    if (!raw) {
      setFilteredDosages([]);
      setDosageHighlight(0);
      return;
    }
    const stored = JSON.parse(localStorage.getItem("allDescription")) || [];
    setFilteredDosages(
      stored
        .filter((d) => d[0]?.toLowerCase().startsWith(q.toLowerCase()))
        .slice(0, 10),
    );
  };
  const selectDosage = (o) => {
    setForm((p) => ({ ...p, dosage: o[0] }));
    setDosageSearch(o[0]);
    setFilteredDosages([]);
    setDosageHighlight(0);
    setTimeout(
      () => document.getElementById(`em-frequency-${index}`)?.focus(),
      0,
    );
  };
  const handleDosageKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setDosageHighlight((p) => (p < filteredDosages.length - 1 ? p + 1 : 0));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setDosageHighlight((p) => (p > 0 ? p - 1 : filteredDosages.length - 1));
    } else if (e.key === "Tab" || e.key === "Enter") {
      e.preventDefault();
      const t = filteredDosages[dosageHighlight];
      if (t) selectDosage(t);
      else
        setTimeout(
          () => document.getElementById(`em-frequency-${index}`)?.focus(),
          0,
        );
    }
  };

  const handleSave = () => {
    if (!form.name.trim()) {
      showToast("Medicine name is required", "ERROR");
      return;
    }
    onSave(form);
  };

  const optionSx = (highlight, i) => ({
    ...menuItemStyle,
    "&:hover": {
      backgroundColor: "neutral.dropDownMenu",
      borderRadius: "16px",
      color: "black !important",
    },
    ...(highlight === i && {
      backgroundColor: "neutral.dropDownMenu",
      borderRadius: "16px",
      color: "black !important",
    }),
  });

  return (
    <>
      {/* Row 1 — index · Medicine · Dosage · Frequency · [Actions rowSpan=2] */}
      <TableRow sx={{ backgroundColor: "#F9FEFF" }}>
        <TableCell sx={{ ...r1, width: 36, textAlign: "center", px: "6px" }}>
          <Typography
            sx={{
              fontFamily: "Lato",
              fontWeight: 700,
              fontSize: "12px",
              color: "#0097B2",
            }}
          >
            {index + 1}
          </Typography>
        </TableCell>

        <TableCell sx={{ ...r1, width: "34%" }}>
          <Autocomplete
            freeSolo
            clearIcon={null}
            options={filteredMeds}
            open={medOpen && filteredMeds.length > 0}
            onOpen={() => setMedOpen(true)}
            onClose={() => setMedOpen(false)}
            inputValue={medSearch}
            getOptionLabel={(o) => (Array.isArray(o) ? o[1] : o)}
            filterOptions={(o) => o}
            PopperComponent={StyledPopper}
            onChange={(_, val) => {
              if (Array.isArray(val)) selectMed(val);
            }}
            sx={autoSx}
            renderInput={(params) => (
              <TextField
                {...params}
                autoFocus
                placeholder="Medicine *"
                inputProps={{ ...params.inputProps, id: `em-name-${index}` }}
                onChange={handleMedSearch}
                onKeyDown={handleMedKeyDown}
                onFocus={(e) => e.target.select()}
              />
            )}
            renderOption={(props, option, { index: i }) => (
              <MenuItem
                {...props}
                key={option[0]}
                sx={optionSx(medHighlight, i)}
              >
                {option[1]}
              </MenuItem>
            )}
          />
        </TableCell>

        <TableCell sx={{ ...r1, width: "20%" }}>
          <Autocomplete
            freeSolo
            clearIcon={null}
            options={filteredDosages}
            open={dosageOpen && filteredDosages.length > 0}
            onOpen={() => setDosageOpen(true)}
            onClose={() => setDosageOpen(false)}
            inputValue={dosageSearch}
            getOptionLabel={(o) => (Array.isArray(o) ? o[0] : o)}
            filterOptions={(o) => o}
            PopperComponent={StyledPopper}
            onChange={(_, val) => {
              if (Array.isArray(val)) selectDosage(val);
            }}
            sx={autoSx}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Dosage"
                inputProps={{ ...params.inputProps, id: `em-dosage-${index}` }}
                onChange={handleDosageSearch}
                onKeyDown={handleDosageKeyDown}
                onFocus={(e) => e.target.select()}
              />
            )}
            renderOption={(props, option, { index: i }) => (
              <MenuItem
                {...props}
                key={option[0]}
                sx={optionSx(dosageHighlight, i)}
              >
                {option[0]}
              </MenuItem>
            )}
          />
        </TableCell>

        <TableCell sx={{ ...r1, width: "22%" }}>
          <TextField
            fullWidth
            placeholder="Frequency"
            value={form.frequency}
            onChange={(e) => set("frequency")(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Tab") {
                e.preventDefault();
                document.getElementById(`em-doctor-${index}`)?.focus();
              }
            }}
            sx={plainInputSx}
            inputProps={{ id: `em-frequency-${index}` }}
          />
        </TableCell>

        {/* rowSpan=2 action cell */}
        <TableCell
          rowSpan={2}
          sx={{
            width: 68,
            px: 0.5,
            py: 0,
            verticalAlign: "middle",
            textAlign: "center",
            backgroundColor: "#F9FEFF",
            borderBottom: "1px solid #efefef",
          }}
        >
          <Tooltip title="Save" arrow>
            <IconButton
              size="small"
              onClick={handleSave}
              disabled={saving}
              sx={{
                color: "#0097B2",
                p: "3px",
                "&:hover": { backgroundColor: "#E0F5F9" },
              }}
            >
              {saving ? (
                <CircularProgress size={13} sx={{ color: "#0097B2" }} />
              ) : (
                <CheckCircleOutlineIcon sx={{ fontSize: 18 }} />
              )}
            </IconButton>
          </Tooltip>
          <Tooltip title="Cancel" arrow>
            <IconButton
              size="small"
              onClick={onCancel}
              sx={{
                color: "#aaa",
                p: "3px",
                "&:hover": { backgroundColor: "#f5f5f5" },
              }}
            >
              <CloseIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>

      {/* Row 2 — Doctor Name · Doctor Details */}
      <TableRow sx={{ backgroundColor: "#F4FCFE" }}>
        <TableCell
          sx={{
            ...r2,
            width: 36,
            px: "6px",
            borderLeft: "2px solid #0097B225",
          }}
        />
        <TableCell sx={{ ...r2, width: "34%" }}>
          <TextField
            fullWidth
            placeholder="Doctor Name"
            value={form.doctor_name}
            onChange={(e) => set("doctor_name")(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Tab") {
                e.preventDefault();
                document.getElementById(`em-details-${index}`)?.focus();
              }
            }}
            sx={plainInputSx}
            inputProps={{ id: `em-doctor-${index}` }}
          />
        </TableCell>
        <TableCell colSpan={2} sx={{ ...r2 }}>
          <TextField
            fullWidth
            placeholder="Doctor Details"
            value={form.doctor_details}
            onChange={(e) => set("doctor_details")(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSave();
            }}
            sx={plainInputSx}
            inputProps={{ id: `em-details-${index}` }}
          />
        </TableCell>
      </TableRow>
    </>
  );
};

// ─── Read-only display rows ───────────────────────────────────────────────────
const DisplayRow = ({ med, onEdit, onDelete }) => (
  <>
    {/* Row 1 */}
    <TableRow
      sx={{
        opacity: med.is_active ? 1 : 0.5,
        "& td": { borderBottom: "none" },
        "&:hover td": { backgroundColor: "#F4FCFE" },
      }}
    >
      <TableCell sx={{ ...r1, width: 36, px: "6px", textAlign: "center" }}>
        <Box
          sx={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            mx: "auto",
            backgroundColor: med.is_active ? "#0097B2" : "#ccc",
          }}
        />
      </TableCell>
      <TableCell sx={{ ...r1, width: "34%" }}>
        <Typography
          sx={{
            fontFamily: "Lato",
            fontWeight: 600,
            fontSize: "13px",
            color: "#1A1A1A",
            lineHeight: 1.3,
          }}
        >
          {med.name}
        </Typography>
      </TableCell>
      <TableCell sx={{ ...r1, width: "20%" }}>
        <Typography
          sx={{
            fontFamily: "Lato",
            fontSize: "13px",
            color: "#555",
            lineHeight: 1.3,
          }}
        >
          {med.dosage || "—"}
        </Typography>
      </TableCell>
      <TableCell sx={{ ...r1, width: "22%" }}>
        <Typography
          sx={{
            fontFamily: "Lato",
            fontSize: "13px",
            color: "#555",
            lineHeight: 1.3,
          }}
        >
          {med.frequency || "—"}
        </Typography>
      </TableCell>

      {/* rowSpan=2 action cell */}
      <TableCell
        rowSpan={2}
        sx={{
          width: 68,
          px: 0.5,
          py: 0,
          verticalAlign: "middle",
          textAlign: "center",
          borderBottom: "1px solid #efefef",
        }}
      >
        <Tooltip title="Edit" arrow>
          <IconButton
            size="small"
            onClick={() => onEdit(med)}
            sx={{
              color: "#0097B2",
              p: "3px",
              "&:hover": { backgroundColor: "#E0F5F9" },
            }}
          >
            <EditOutlinedIcon sx={{ fontSize: 15 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Remove" arrow>
          <IconButton
            size="small"
            onClick={() => onDelete(med)}
            sx={{
              color: "#FF392B",
              p: "3px",
              "&:hover": { backgroundColor: "#FFF0EF" },
            }}
          >
            <DeleteOutlineIcon sx={{ fontSize: 15 }} />
          </IconButton>
        </Tooltip>
      </TableCell>
    </TableRow>

    {/* Row 2 — doctor info sub-row */}
    <TableRow
      sx={{
        opacity: med.is_active ? 1 : 0.5,
        "& td": { borderBottom: "1px solid #efefef" },
        "&:hover td": { backgroundColor: "#F4FCFE" },
      }}
    >
      <TableCell
        sx={{ ...r2, width: 36, px: "6px", borderLeft: "2px solid #0097B225" }}
      />
      <TableCell sx={{ ...r2, width: "34%" }}>
        <Typography
          sx={{
            fontFamily: "Lato",
            fontSize: "12px",
            color: "#0097B2",
            fontWeight: 500,
            lineHeight: 1.3,
          }}
        >
          {med.doctor_name ? (
            `Dr. ${med.doctor_name}`
          ) : (
            <span style={{ color: "#ccc" }}>—</span>
          )}
        </Typography>
      </TableCell>
      <TableCell colSpan={2} sx={{ ...r2 }}>
        <Typography
          sx={{
            fontFamily: "Lato",
            fontSize: "12px",
            color: "#999",
            lineHeight: 1.3,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            maxWidth: 300,
          }}
        >
          {med.doctor_details || ""}
        </Typography>
      </TableCell>
    </TableRow>
  </>
);

// ─── Main Component ────────────────────────────────────────────────────────────
const ExternalMedicines = () => {
  const patientDetailsData = useSelector(
    (state) => state.prescription.patientDetailsData,
  );
  const patientId = patientDetailsData?.id;
  const appointmentData = useSelector(
    (state) => state.prescription.appointmentData,
  );

  const [expanded, setExpanded] = useState(false);
  const [meds, setMeds] = useState([]);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState(null);

  useEffect(() => {
    if (expanded && patientId) loadMeds();
  }, [expanded, patientId]);

  const loadMeds = async () => {
    setFetchLoading(true);
    try {
      const data = await fetchExternalMeds(patientId, appointmentData?.id);
      setMeds(
        Array.isArray(data)
          ? data
          : data?.external_medications ||
              data?.payload?.external_medications ||
              [],
      );
    } catch {
      showToast("Failed to load external medicines", "ERROR");
    } finally {
      setFetchLoading(false);
    }
  };

  const handleStartAdd = () => {
    setEditingId("new");
    setEditData({ ...emptyRow });
  };
  const handleStartEdit = (med) => {
    setEditingId(med.id);
    setEditData({ ...med });
  };
  const handleCancelEdit = () => {
    setEditingId(null);
    setEditData(null);
  };

  const handleSave = async (form) => {
    if (!patientId) {
      showToast("Patient not registered yet", "ERROR");
      return;
    }
    if (!appointmentData?.id) {
      showToast("The patient doesn't have an appointment yet.", "ERROR");
      return;
    }
    const isNew = editingId === "new";
    setSavingId(editingId);
    try {
      if (isNew) {
        await createExternalMed({
          ...form,
          patient_id: patientId,
          appointment_id: appointmentData?.id,
        });
        showToast("External medicine added", "SUCCESS");
      } else {
        await updateExternalMed(editingId, {
          ...form,
          patient_id: patientId,
          appointment_id: appointmentData?.id,
        });
        showToast("External medicine updated", "SUCCESS");
      }
      setEditingId(null);
      setEditData(null);
      loadMeds();
    } catch {
      showToast("Failed to save external medicine", "ERROR");
    } finally {
      setSavingId(null);
    }
  };

  const handleDelete = async (med) => {
    if (!window.confirm(`Remove "${med.name}" from external medicines?`))
      return;
    setSavingId(med.id);
    try {
      await deleteExternalMed(med.id);
      showToast("External medicine removed", "SUCCESS");
      setMeds((prev) => prev.filter((m) => m.id !== med.id));
    } catch {
      showToast("Failed to remove external medicine", "ERROR");
    } finally {
      setSavingId(null);
    }
  };

  const showTable = !fetchLoading && (meds.length > 0 || editingId === "new");

  return (
    <Box mt={3} sx={{ ml: -1 }}>
      <Accordion
        expanded={expanded}
        onChange={(_, val) => setExpanded(val)}
        sx={{
          borderRadius: "10px !important",
          boxShadow: "none",
          border: "1px solid #e0e0e0",
          backgroundColor: "white",
          "&:before": { display: "none" },
          overflow: "hidden",
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: "#0097B2" }} />}
          sx={{
            px: 2,
            minHeight: "56px",
            "&.Mui-expanded": { minHeight: "56px" },
            "& .MuiAccordionSummary-content": { my: "10px !important" },
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box
              sx={{
                width: 36,
                height: 36,
                backgroundColor: "neutral.blackBorder",
                borderRadius: "50%",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flexShrink: 0,
              }}
            >
              <MedicationOutlinedIcon sx={{ color: "#0097B2", fontSize: 20 }} />
            </Box>
            <Typography
              fontWeight={600}
              sx={{ fontFamily: "Lato", fontSize: "14px" }}
            >
              External Medicines
            </Typography>
            {meds.length > 0 && (
              <Chip
                label={meds.length}
                size="small"
                sx={{
                  backgroundColor: "#E0F5F9",
                  color: "#0097B2",
                  fontWeight: 700,
                  fontFamily: "Lato",
                  fontSize: "11px",
                  height: "20px",
                }}
              />
            )}
          </Box>
        </AccordionSummary>

        <AccordionDetails sx={{ px: 0, pt: 0, pb: 1.5 }}>
          {fetchLoading && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={24} sx={{ color: "#0097B2" }} />
            </Box>
          )}

          {!fetchLoading && meds.length === 0 && editingId !== "new" && (
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                py: 3.5,
                gap: 0.75,
              }}
            >
              <MedicationOutlinedIcon sx={{ fontSize: 34, color: "#BBDCE5" }} />
              <Typography
                sx={{ fontFamily: "Lato", fontSize: "13px", color: "#999" }}
              >
                No external medicines recorded
              </Typography>
            </Box>
          )}

          {showTable && (
            <TableContainer sx={{ backgroundColor: "white" }}>
              <Table
                size="small"
                sx={{
                  backgroundColor: "white",
                  "& .MuiTableRow-root": { verticalAlign: "top" },
                }}
              >
                <TableHead>
                  <TableRow sx={{ backgroundColor: "neutral.blackBorder" }}>
                    {[
                      { label: "#", w: 36, align: "center", px: "6px" },
                      { label: "Medicine", w: "34%", align: "left", px: 1 },
                      { label: "Dosage", w: "20%", align: "left", px: 1 },
                      { label: "Frequency", w: "22%", align: "left", px: 1 },
                      { label: "", w: 68, align: "left", px: 1 },
                    ].map(({ label, w, align, px: cellPx }, i) => (
                      <TableCell
                        key={i}
                        sx={{
                          width: w,
                          textAlign: align,
                          fontFamily: "Lato",
                          fontWeight: 700,
                          fontSize: "12px",
                          color: "neutral.black",
                          borderBottom: "1px solid #e0e0e0",
                          py: "7px",
                          px: cellPx,
                        }}
                      >
                        {label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {meds.map((med, idx) =>
                    editingId === med.id ? (
                      <EditableRow
                        key={med.id}
                        index={idx}
                        data={editData}
                        onSave={handleSave}
                        onCancel={handleCancelEdit}
                        saving={savingId === med.id}
                      />
                    ) : (
                      <DisplayRow
                        key={med.id}
                        med={med}
                        onEdit={handleStartEdit}
                        onDelete={handleDelete}
                      />
                    ),
                  )}
                  {editingId === "new" && (
                    <EditableRow
                      index={meds.length}
                      data={emptyRow}
                      onSave={handleSave}
                      onCancel={handleCancelEdit}
                      saving={savingId === "new"}
                    />
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              px: 2,
              pt: showTable ? 1 : 0,
            }}
          >
            <Button
              variant="contained"
              startIcon={<AddIcon sx={{ fontSize: "15px !important" }} />}
              onClick={handleStartAdd}
              disabled={editingId !== null}
              sx={{
                ...tealBtnStyle,
                height: "32px",
                opacity: editingId !== null ? 0.5 : 1,
              }}
            >
              Add Medicine
            </Button>
          </Box>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default ExternalMedicines;
